import {useCallback, useSyncExternalStore} from "react";
import {API} from "../api";

declare global {
  interface Window {
    __saveScene?: (options?: {
      navigate?: boolean;
    }) => Promise<{scene_id?: string; blocked?: string}>;
  }
}

type JetCheckState = "saving" | "analyzing" | {url: string} | {error: string} | null;

// Module-level store — survives component unmount and tab switches
const store = new Map<string, JetCheckState>();
const listeners = new Set<() => void>();
// Pending auto-evict timers keyed by bannerId. Cleared on every state change
// so a stale timer from a previous run can't wipe the current one.
const evictTimers = new Map<string, ReturnType<typeof setTimeout>>();
const AUTO_EVICT_MS = 10 * 60 * 1000;

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function notify() {
  listeners.forEach((fn) => fn());
}

function clearEvictTimer(bannerId: string) {
  const handle = evictTimers.get(bannerId);
  if (handle !== undefined) {
    clearTimeout(handle);
    evictTimers.delete(bannerId);
  }
}

function scheduleEvict(bannerId: string) {
  clearEvictTimer(bannerId);
  evictTimers.set(
    bannerId,
    setTimeout(() => {
      evictTimers.delete(bannerId);
      deleteEntry(bannerId);
    }, AUTO_EVICT_MS)
  );
}

function setEntry(bannerId: string, value: JetCheckState) {
  clearEvictTimer(bannerId);
  store.set(bannerId, value);
  notify();
}

function deleteEntry(bannerId: string) {
  clearEvictTimer(bannerId);
  store.delete(bannerId);
  notify();
}

/**
 * Hook for sending a banner to Jet Check AI analysis.
 *
 * Flow: auto-save the scene (via `window.__saveScene`) to get a fresh sceneId,
 * then POST {sceneId, templateId} to the server — the server renders the
 * single banner via the Playwright pool and forwards the PNG to jet.labs.jb.gg.
 *
 * State persists across tab switches via module-level store.
 * useSyncExternalStore with a per-id selector avoids re-rendering siblings.
 */
export function useJetCheck(bannerId: string) {
  const jetState = useSyncExternalStore(subscribe, () => store.get(bannerId) ?? null);

  const runJetCheck = useCallback(async () => {
    setEntry(bannerId, "saving");

    try {
      if (!window.__saveScene) {
        throw new Error("Save-scene bridge not available — is App mounted?");
      }
      const saveResult = await window.__saveScene();
      if (saveResult?.blocked) {
        // Dialog already shown by __saveScene — reset to idle, no error toast.
        deleteEntry(bannerId);
        return;
      }
      const sceneId = saveResult?.scene_id;
      if (!sceneId) {
        throw new Error("Failed to save scene");
      }

      setEntry(bannerId, "analyzing");

      const result = await API.jetCheck(sceneId, bannerId);
      setEntry(bannerId, {url: result.url});
      scheduleEvict(bannerId);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error("[JetCheck] Failed:", error);
      setEntry(bannerId, {error: message});
      scheduleEvict(bannerId);
    }
  }, [bannerId]);

  const dismissJetCheck = useCallback(() => {
    deleteEntry(bannerId);
  }, [bannerId]);

  return {runJetCheck, dismissJetCheck, jetState};
}
